import { UnleashClient } from 'unleash-proxy-client';

import type { UnleashConfig } from '@/common/overmind/namespaces/arcgis/config/config.types';

export type IFeatureFlagsReader = {
  readonly read: (featureFlagName: string) => boolean;
  // NOTE: leaky abstraction as `startFor` shouldn't take care `config` into account
  // TODO: split responsibilities into `ISetupFeatureFlagsReader` and `IFeatureFlagsReader`
  readonly getVariant: <T>(featureFlagName: string) => T | null;
  readonly startFor: (
    config: UnleashConfig | undefined,
    identityProvider: string,
    userId: string,
    tenantId: string,
    onFeatureToggledChange: () => void,
    onFeatureToggledReset: () => void
  ) => Promise<void>;
};

export let unleashClient: UnleashClient | null;
const isUnleashAvailable = (unleashClient: UnleashClient | null) =>
  Boolean(unleashClient);
const isUnleashConfigAvailable = (config: UnleashConfig | undefined) =>
  Boolean(config);

const featureToggleReader: IFeatureFlagsReader = {
  getVariant: <T>(featureFlagName: string) => {
    if (!isUnleashAvailable(unleashClient)) return null;
    const variant = unleashClient?.getVariant(featureFlagName);
    if (!variant?.enabled || !variant?.payload?.value) {
      return null;
    }

    return JSON.parse(variant.payload.value) as T;
  },
  read(featureFlagName) {
    if (!isUnleashAvailable(unleashClient)) return false;
    return Boolean(unleashClient?.isEnabled(featureFlagName));
  },
  startFor: async (
    config,
    identityProvider,
    userId,
    tenantId,
    onFeatureToggledChange?: () => void,
    onFeatureToggledReset?: () => void
  ) => {
    if (!isUnleashConfigAvailable(config)) return;
    unleashClient =
      unleashClient || new UnleashClient(config as unknown as UnleashConfig);
    if (!isUnleashAvailable(unleashClient)) return;

    unleashClient?.setContextField('userId', userId);
    unleashClient?.setContextField('identityProvider', identityProvider);
    unleashClient?.setContextField('tenantId', tenantId);

    onFeatureToggledReset && onFeatureToggledReset();
    await loadFeatureTogglesOnce();
    onFeatureToggledChange && onFeatureToggledChange();
  }
} as const;

export default featureToggleReader;
async function loadFeatureTogglesOnce() {
  await unleashClient?.stop();
  await unleashClient?.start();
  await unleashClient?.stop();
}
